import type { SupabaseClient } from "@supabase/supabase-js";
import {
  ACHIEVEMENTS,
  lookupAchievement,
  newlyEarnedIds,
  type UserStats,
} from "./achievements";
import { XP_AWARD } from "./leveling";
import type { Database, SubscriptionTier } from "./types";

/**
 * Award engine.
 *
 * Two public functions:
 *
 *   `bumpXp(supabase, userId, amount)` — credit XP from a primitive
 *   action (review graded, exam submitted, etc.). Updates the
 *   denormalised `level` column on profiles so the dashboard doesn't
 *   need to recompute. Returns the new totals plus a list of any
 *   freshly-unlocked level-milestone achievements (level_5, _10, _25)
 *   the caller should toast.
 *
 *   `awardAchievements(supabase, userId, stats)` — full scan. Reads
 *   existing unlocks, computes catalog matches against the supplied
 *   stats snapshot, writes new rows, and credits each badge's XP
 *   bonus on top. Returns the freshly unlocked badge ids and the
 *   new XP/level totals so the action handler can return them to
 *   the client for toast rendering.
 *
 * Both functions are no-throw at the API boundary: if Supabase is
 * unreachable we log + return zero-impact results so the calling
 * action (gradeCard, submitExam, etc.) doesn't fail because the
 * gamification layer hiccupped.
 *
 * The functions take a `SupabaseClient<Database>` — the same instance
 * the action handler already has open. We deliberately don't create
 * a service-role client inside the engine: callers running in the
 * user's RLS context (e.g. server actions) sometimes want unlocks
 * to be RLS-scoped, and callers running with service_role (webhook,
 * cron) want bypass. Passing the client makes the choice explicit.
 */

export interface AwardOutcome {
  /** Achievement ids that were newly written this call. */
  newlyUnlocked: string[];
  /** XP credited by this call (action XP + sum of unlocked badge XP). */
  xpAwarded: number;
  /** Total XP after the call. */
  xpTotal: number;
  /** Level after the call. */
  level: number;
}

const EMPTY: AwardOutcome = {
  newlyUnlocked: [],
  xpAwarded: 0,
  xpTotal: 0,
  level: 1,
};

type Client = SupabaseClient<Database>;

/**
 * Increment XP by `amount` (≥0) and resync `level`. Routes through the
 * `bump_xp_with_periods` RPC (migration 0019) so the per-week and
 * per-month leaderboard counters advance atomically with xp_total.
 * No-op + log if Supabase reports an error so a transient outage
 * never breaks a graded review.
 */
export async function bumpXp(
  supabase: Client,
  userId: string,
  amount: number,
): Promise<AwardOutcome> {
  if (!Number.isFinite(amount) || amount <= 0) {
    return readCurrent(supabase, userId).catch(() => EMPTY);
  }
  // RPC runs SECURITY INVOKER and reads auth.uid() — the userId arg
  // is informational for the caller's logs, not used in the SQL.
  const { data, error } = await supabase.rpc("bump_xp_with_periods", {
    p_amount: amount,
  });
  if (error) {
    console.warn("[gamification] bumpXp RPC failed", error.message);
    return EMPTY;
  }
  const row = Array.isArray(data) ? data[0] : null;
  if (!row) {
    console.warn("[gamification] bumpXp RPC returned no row");
    return EMPTY;
  }
  return {
    newlyUnlocked: [],
    xpAwarded: amount,
    xpTotal: row.xp_total,
    level: row.level,
  };
}

/**
 * Full scan: writes any newly-met badges plus their XP bonuses.
 * `stats` should already be computed by the caller (gradeCard /
 * submitExam) since each caller has cheap access to its own counters.
 */
export async function awardAchievements(
  supabase: Client,
  userId: string,
  stats: UserStats,
): Promise<AwardOutcome> {
  // Load existing unlock ids to suppress already-earned badges.
  const { data: existing, error: existingErr } = await supabase
    .from("achievement_unlocks")
    .select("achievement_id")
    .eq("user_id", userId);
  if (existingErr) {
    console.warn(
      "[gamification] awardAchievements unlock read failed",
      existingErr.message,
    );
    return EMPTY;
  }
  const have = new Set((existing ?? []).map((r) => r.achievement_id));
  const ids = newlyEarnedIds(stats, have);
  if (ids.length === 0) {
    // No new badges — surface current XP/level so the caller can still
    // animate the bar.
    const current = await readCurrent(supabase, userId).catch(() => EMPTY);
    return current;
  }

  const bonusXp = ids.reduce(
    (sum, id) => sum + (lookupAchievement(id)?.xp ?? 0),
    0,
  );

  // Insert unlock rows. `onConflict: do nothing` keeps us idempotent
  // if two parallel actions both detect the same badge.
  const { error: insertErr } = await supabase
    .from("achievement_unlocks")
    .upsert(
      ids.map((id) => ({
        user_id: userId,
        achievement_id: id,
        xp_awarded: lookupAchievement(id)?.xp ?? 0,
      })),
      { onConflict: "user_id,achievement_id", ignoreDuplicates: true },
    );
  if (insertErr) {
    console.warn(
      "[gamification] awardAchievements insert failed",
      insertErr.message,
    );
    return EMPTY;
  }

  const post = await bumpXp(supabase, userId, bonusXp);
  return {
    newlyUnlocked: ids,
    xpAwarded: bonusXp,
    xpTotal: post.xpTotal,
    level: post.level,
  };
}

async function readCurrent(supabase: Client, userId: string): Promise<AwardOutcome> {
  const { data, error } = await supabase
    .from("profiles")
    .select("xp_total, level")
    .eq("id", userId)
    .maybeSingle();
  if (error || !data) return EMPTY;
  return {
    newlyUnlocked: [],
    xpAwarded: 0,
    xpTotal: data.xp_total ?? 0,
    level: data.level ?? 1,
  };
}

/**
 * Computes the UserStats snapshot a single Supabase round trip per
 * counter. Callers (server actions) use this when they want to award
 * achievements but don't already have the counters in scope.
 *
 * For per-action hot paths (gradeCard), callers prefer to construct
 * UserStats incrementally from data they already loaded; this helper
 * is for occasional/batch awards.
 */
export async function computeUserStats(
  supabase: Client,
  userId: string,
  tier: SubscriptionTier,
): Promise<UserStats> {
  const [
    profileResult,
    reviewsResult,
    bookmarksResult,
    blockResult,
    atsaResult,
    trainerResult,
  ] = await Promise.all([
    supabase
      .from("profiles")
      .select(
        "xp_total, study_streak_days, study_streak_longest_days",
      )
      .eq("id", userId)
      .maybeSingle(),
    supabase
      .from("reviews")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId),
    supabase
      .from("card_states")
      .select("user_id", { count: "exact", head: true })
      .eq("user_id", userId)
      .not("bookmarked_at", "is", null),
    supabase
      .from("block_prep_attempts")
      .select("preset, correct_count, question_count, submitted_at")
      .eq("user_id", userId)
      .not("submitted_at", "is", null),
    supabase
      .from("atsa_attempts")
      .select("module, score, submitted_at")
      .eq("user_id", userId)
      .not("submitted_at", "is", null),
    supabase
      .from("phraseology_attempts")
      .select("forbidden_hits", { count: "exact" })
      .eq("user_id", userId),
  ]);

  const profile = profileResult.data;
  const blockAttempts = blockResult.data ?? [];
  const atsaAttempts = atsaResult.data ?? [];
  const trainerRows = trainerResult.data ?? [];

  const blockPrepDistinctPresets = new Set(
    blockAttempts.map((a) => a.preset).filter(Boolean),
  ).size;
  const blockPrepBestScore = blockAttempts.reduce((best, a) => {
    if (!a.question_count || a.question_count <= 0) return best;
    const score = (a.correct_count ?? 0) / a.question_count;
    return Math.max(best, score);
  }, 0);

  const atsaDistinctModules = new Set(
    atsaAttempts.map((a) => a.module).filter(Boolean),
  ).size;
  const atsaMockBestScore = atsaAttempts
    .filter((a) => a.module === "mock_full")
    .reduce((best, a) => Math.max(best, a.score ?? 0), 0);
  const atsaAnyBestScore = atsaAttempts.reduce(
    (best, a) => Math.max(best, a.score ?? 0),
    0,
  );

  const trainerCleanAttempts = trainerRows.filter((r) => {
    const hits = r.forbidden_hits as unknown;
    return Array.isArray(hits) && hits.length === 0;
  }).length;

  return {
    streakDays: profile?.study_streak_days ?? 0,
    longestStreak: profile?.study_streak_longest_days ?? 0,
    totalReviews: reviewsResult.count ?? 0,
    bookmarkedCards: bookmarksResult.count ?? 0,
    tier,
    xp: profile?.xp_total ?? 0,
    blockPrepSubmitted: blockAttempts.length,
    blockPrepDistinctPresets,
    blockPrepBestScore,
    atsaDistinctModules,
    atsaSubmitted: atsaAttempts.length,
    atsaMockBestScore,
    atsaAnyBestScore,
    trainerAttempts: trainerResult.count ?? 0,
    trainerCleanAttempts,
    // Engagement fields are caller-provided when the caller has cheap
    // access; the default-false here means engagement badges only fire
    // when explicitly checked from an action that knows the context
    // (e.g. gradeCard knows the local hour from the request).
    lastReviewLocalHour: null,
    reviewedBothWeekendDays: false,
  };
}

/** Convenience constant export for hot-path callers that want to
 *  reference the XP table in their own action code. */
export { XP_AWARD, ACHIEVEMENTS };
